import React, { useState, useEffect, useRef } from 'react'
import { FormGroup, Label, Input, Button, Spinner, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, ListGroupItem } from 'reactstrap'
import { useSnackbar } from 'notistack'
import { ShoppingCart, DollarSign, Save, Eye, ChevronDown, Package, Truck, Check } from 'react-feather'
import {
  useSaveAutoNumberConfigMutation,
  useLazyPreviewAutoNumberConfigQuery,
} from '../settingApiSlice'

const ConfigRow = ({ type, config, otherConfigs = [], refetch }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [pattern, setPattern] = useState(config?.pattern || '')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [cursorPos, setCursorPos] = useState(0)
  const inputRef = useRef(null)

  const insertTag = (tag) => {
    const input = inputRef.current
    if (input) {
      const start = input.selectionStart || 0
      const end = input.selectionEnd || 0

      const textBeforeCursor = pattern.substring(0, start)
      const lastOpenBrace = textBeforeCursor.lastIndexOf('{')
      const lastCloseBrace = textBeforeCursor.lastIndexOf('}')

      let replaceStart = start
      if (lastOpenBrace !== -1 && lastOpenBrace > lastCloseBrace) {
        replaceStart = lastOpenBrace
      }

      const newValue = pattern.substring(0, replaceStart) + tag + pattern.substring(end)
      setPattern(newValue)

      setTimeout(() => {
        input.focus()
        input.setSelectionRange(replaceStart + tag.length, replaceStart + tag.length)
        setCursorPos(replaceStart + tag.length)
      }, 0)
    } else {
      setPattern(prev => prev + tag)
    }
  }

  const now = new Date();
  const exM = now.getMonth() + 1;
  const exMM = String(exM).padStart(2, '0');
  const exMMM = now.toLocaleString('id-ID', { month: 'short' });
  const exMMMM = now.toLocaleString('id-ID', { month: 'long' });
  const exYY = String(now.getFullYear()).slice(2);
  const exYYYY = now.getFullYear();

  const availableTags = [
    { label: `Angka bulan (contoh: ${exM})`, value: '{M}', type: 'month' },
    { label: `Angka bulan dengan awalan nol (contoh: ${exMM})`, value: '{MM}', type: 'month' },
    { label: `Singkatan nama bulan (contoh: ${exMMM})`, value: '{MMM}', type: 'month' },
    { label: `Nama bulan penuh (contoh: ${exMMMM})`, value: '{MMMM}', type: 'month' },
    { label: `Tahun 2 digit (contoh: ${exYY})`, value: '{YY}', type: 'year' },
    { label: `Tahun 4 digit (contoh: ${exYYYY})`, value: '{YYYY}', type: 'year' },
    { label: 'Nomor urut auto increment', value: '{SEQ}', type: 'seq' }
  ]

  useEffect(() => {
    if (config?.pattern && !pattern) {
      setPattern(config.pattern)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  const [triggerPreview, { data: previewData, isFetching: isPreviewFetching }] = useLazyPreviewAutoNumberConfigQuery()

  const handlePreview = () => {
    if (pattern.trim()) {
      triggerPreview({ type, pattern })
    }
  }

  const [saveConfig, { isLoading: isSaving }] = useSaveAutoNumberConfigMutation()

  const handleSave = async () => {
    if (!pattern.trim()) {
      enqueueSnackbar('Pattern tidak boleh kosong', { variant: 'error' })
      return
    }

    const isDuplicate = otherConfigs?.some(c => c?.pattern === pattern.trim())
    if (isDuplicate) {
      enqueueSnackbar('Format tidak boleh sama dengan transaksi lainnya', { variant: 'error' })
      return
    }

    try {
      await saveConfig({ type, pattern }).unwrap()
      enqueueSnackbar('Berhasil menyimpan konfigurasi', { variant: 'success' })
      refetch()
    } catch (err) {
      enqueueSnackbar(err?.data?.message || 'Gagal menyimpan konfigurasi', { variant: 'error' })
    }
  }

  let Icon = DollarSign
  let themeColor = '#198754'
  let typeLabel = ''

  if (type === 'BUY') {
    Icon = ShoppingCart
    themeColor = '#0d6efd'
    typeLabel = 'Pembelian'
  } else if (type === 'SELL') {
    Icon = DollarSign
    themeColor = '#198754'
    typeLabel = 'Penjualan'
  } else if (type === 'INV') {
    Icon = Package
    themeColor = '#6f42c1'
    typeLabel = 'Pembuatan Inventori'
  } else if (type === 'XFER') {
    Icon = Truck
    themeColor = '#fd7e14'
    typeLabel = 'Transfer Inventori'
  }

  const isDuplicate = pattern.trim() && otherConfigs?.some(c => c?.pattern === pattern.trim())

  return (
    <ListGroupItem className="py-4 px-3 px-md-4 border-bottom border-0">
      <div className="d-flex flex-column flex-xl-row align-items-xl-start gap-3 gap-xl-4">
        {/* Kolom Kiri: Info Transaksi */}
        <div style={{ minWidth: '260px' }} className="d-flex align-items-center gap-3 flex-shrink-0">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{ width: '42px', height: '42px', backgroundColor: `${themeColor}15`, color: themeColor }}
          >
            <Icon size={20} />
          </div>
          <div>
            <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>
              {type === 'BUY' || type === 'SELL' ? `Transaksi ${typeLabel}` : typeLabel}
            </div>
            <div className="text-muted" style={{ fontSize: '13px' }}>
              {config?.pattern ? (
                <span className="text-success"><Check size={12} className="me-1" />Terkonfigurasi</span>
              ) : 'Belum diatur'}
            </div>
          </div>
        </div>

        {/* Kolom Tengah: Input & Preview */}
        <div className="flex-grow-1" style={{ minWidth: '0' }}>
          <div className="d-flex flex-column flex-md-row align-items-md-start gap-3">
            <div className="flex-grow-1 position-relative">
              <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)} className="w-100">
                <DropdownToggle tag="div" className="p-0 border-0 bg-transparent w-100">
                  <div
                    className="d-flex align-items-center bg-white"
                    style={{
                      border: `1px solid ${isDuplicate ? '#dc3545' : (dropdownOpen ? '#86b7fe' : '#ced4da')}`,
                      borderRadius: '8px',
                      boxShadow: dropdownOpen ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
                      transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!dropdownOpen) setDropdownOpen(true);
                      inputRef.current?.focus();
                    }}
                  >
                    <Input
                      data-testid={`setting-autonumber-pattern-input-${type.toLowerCase()}`}
                      innerRef={inputRef}
                      id={`pattern-${type}`}
                      name={`pattern-${type}`}
                      type="text"
                      value={pattern}
                      onChange={(e) => {
                        setPattern(e.target.value);
                        setCursorPos(e.target.selectionStart || 0);
                        if (!dropdownOpen) setDropdownOpen(true);
                      }}
                      onKeyUp={(e) => setCursorPos(e.target.selectionStart || 0)}
                      onMouseUp={(e) => setCursorPos(e.target.selectionStart || 0)}
                      onFocus={(e) => {
                        setDropdownOpen(true);
                        setCursorPos(e.target.selectionStart || 0);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCursorPos(e.target.selectionStart || 0);
                      }}
                      placeholder="Masukkan format nomor transaksi..."
                      className="py-2 border-0 shadow-none bg-transparent"
                      style={{ borderRadius: '8px', flexGrow: 1 }}
                      autoComplete="off"
                    />
                  </div>
                </DropdownToggle>

                <DropdownMenu className="w-100 border-0 shadow mt-1" style={{ maxHeight: '250px', overflowY: 'auto', borderRadius: '8px', border: '1px solid #ced4da' }}>
                  <DropdownItem header style={{ fontSize: '11px', fontWeight: 'bold', color: '#adb5bd', letterSpacing: '0.5px' }}>
                    VARIABEL TERSEDIA
                  </DropdownItem>
                  {(() => {
                    const textBeforeCursor = pattern.substring(0, cursorPos)
                    const lastOpenBrace = textBeforeCursor.lastIndexOf('{')
                    const lastCloseBrace = textBeforeCursor.lastIndexOf('}')
                    let currentSearchStr = ''
                    if (lastOpenBrace !== -1 && lastOpenBrace > lastCloseBrace) {
                      currentSearchStr = textBeforeCursor.substring(lastOpenBrace)
                    }

                    const usedTypes = availableTags
                      .filter(t => pattern.includes(t.value))
                      .map(t => t.type)

                    const filteredTags = availableTags.filter(tag =>
                      !usedTypes.includes(tag.type) &&
                      (tag.label.toLowerCase().includes(currentSearchStr.toLowerCase()) || tag.value.toLowerCase().includes(currentSearchStr.toLowerCase()))
                    );

                    return filteredTags.length > 0 ? (
                      filteredTags.map(tag => (
                        <DropdownItem
                          key={tag.value}
                          onClick={() => {
                            insertTag(tag.value)
                            setDropdownOpen(false)
                          }}
                          className="py-2 px-3"
                        >
                          <span className="text-dark fw-bold">{tag.value}</span> <span className="text-muted ms-1">({tag.label})</span>
                        </DropdownItem>
                      ))
                    ) : (
                      <DropdownItem disabled className="py-2 text-muted text-center">
                        {currentSearchStr ? 'Variabel tidak ditemukan' : 'Semua variabel telah digunakan'}
                      </DropdownItem>
                    )
                  })()}
                </DropdownMenu>
              </Dropdown>

              {isDuplicate && (
                <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                  Format tidak boleh sama dengan transaksi lainnya
                </div>
              )}

              <div className="mt-2 d-flex justify-content-between align-items-center">
                <div className="text-muted" style={{ fontSize: '13px' }}>
                  {isPreviewFetching ? (
                    <span className="text-primary"><Spinner size="sm" className="me-2" />Memuat preview...</span>
                  ) : previewData?.result ? (
                    <span>Preview: <span className="text-dark fw-bold px-2 py-1 ms-1" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', fontFamily: 'monospace' }}>{previewData.result}</span></span>
                  ) : null}
                </div>

                <div
                  data-testid={`setting-autonumber-example-${type.toLowerCase()}`}
                  className="px-2 py-1 rounded text-primary"
                  style={{ 
                    fontSize: '11px', 
                    cursor: 'pointer', 
                    backgroundColor: '#e8f4fd', 
                    border: '1px solid #b6d4fe',
                    fontWeight: '600',
                    transition: 'all 0.2s ease-in-out'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#d0e7ff' }}
                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#e8f4fd' }}
                  onClick={() => {
                    const ex = (type === 'INV' || type === 'XFER') 
                      ? `${type}/{YYYY}/{MM}/{SEQ}` 
                      : `TRX/${type}/{YYYY}/{MM}/{SEQ}`;
                    setPattern(ex);
                  }}
                  title="Klik untuk menerapkan format contoh ini"
                >
                  Gunakan contoh: <span style={{ fontFamily: 'monospace' }}>
                    {(type === 'INV' || type === 'XFER') 
                      ? `${type}/{YYYY}/{MM}/{SEQ}` 
                      : `TRX/${type}/{YYYY}/{MM}/{SEQ}`}
                  </span>
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 flex-shrink-0">
              <Button color="secondary" outline data-testid={`setting-autonumber-preview-${type.toLowerCase()}`} onClick={handlePreview} disabled={!pattern.trim() || isPreviewFetching} style={{ borderRadius: '8px', padding: '8px 16px' }}>
                <Eye size={16} className="me-2" />
                Cek Hasil
              </Button>
              <Button
                color="primary"
                data-testid={`setting-autonumber-save-${type.toLowerCase()}`}
                onClick={handleSave}
                disabled={isSaving || !pattern.trim() || isDuplicate}
                className="d-flex align-items-center justify-content-center shadow-sm"
                style={{ borderRadius: '8px', minWidth: '110px', padding: '8px 16px' }}
              >
                {isSaving ? <Spinner size="sm" /> : <Save size={16} className="me-2" />}
                Simpan
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ListGroupItem>
  )
}

export default ConfigRow
